// Cognito Authentication Service for Admin Panel
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

// Cognito configuration - same pool as chat app
const COGNITO_CONFIG = {
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || 'us-east-1_xxxxxxxx',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || 'xxxxxxxxxxxxxxxxxxxxxxxxxx',
  region: import.meta.env.VITE_REGION || 'us-east-1',
};

const poolData = {
  UserPoolId: COGNITO_CONFIG.userPoolId,
  ClientId: COGNITO_CONFIG.clientId,
};

const userPool = new CognitoUserPool(poolData);

export interface AuthResult {
  success: boolean;
  error?: string;
  requiresMFA?: boolean;
  session?: CognitoUserSession;
}

export interface CognitoUserInfo {
  email: string;
  userId: string;
  givenName?: string;
  familyName?: string;
  tenantId?: string;
  role?: string;
}

class CognitoAuthService {
  private currentUser: CognitoUser | null = null;

  /**
   * Check authentication status asynchronously
   */
  async checkAuth(): Promise<boolean> {
    const user = userPool.getCurrentUser();
    if (!user) return false;

    return new Promise((resolve) => {
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(false);
        } else {
          resolve(session.isValid());
        }
      });
    });
  }

  /**
   * Sign in user
   */
  async signIn(email: string, password: string): Promise<AuthResult> {
    return new Promise((resolve) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      this.currentUser = cognitoUser;

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          localStorage.setItem('userEmail', email);
          resolve({ success: true, session });
        },
        onFailure: (err: Error) => {
          resolve({ success: false, error: err.message });
        },
        mfaRequired: () => {
          resolve({ success: false, requiresMFA: true });
        },
        newPasswordRequired: () => {
          resolve({ success: false, error: 'New password required' });
        },
      });
    });
  }

  /**
   * Confirm MFA code
   */
  async confirmMFA(code: string): Promise<AuthResult> {
    if (!this.currentUser) {
      return { success: false, error: 'No user session' };
    }

    return new Promise((resolve) => {
      this.currentUser!.sendMFACode(code, {
        onSuccess: (session: CognitoUserSession) => {
          resolve({ success: true, session });
        },
        onFailure: (err: Error) => {
          resolve({ success: false, error: err.message });
        },
      });
    });
  }

  /**
   * Sign out user
   */
  signOut(): void {
    const user = userPool.getCurrentUser();
    if (user) {
      user.signOut();
    }
    localStorage.removeItem('userEmail');
    this.currentUser = null;
  }

  /**
   * Get current session
   */
  async getSession(): Promise<CognitoUserSession | null> {
    const user = userPool.getCurrentUser();
    if (!user) return null;

    return new Promise((resolve) => {
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(null);
        } else {
          resolve(session);
        }
      });
    });
  }

  /**
   * Get access token
   */
  async getAccessToken(): Promise<string | null> {
    const session = await this.getSession();
    if (!session) return null;
    return session.getAccessToken().getJwtToken();
  }

  /**
   * Get ID token
   */
  async getIdToken(): Promise<string | null> {
    const session = await this.getSession();
    if (!session) return null;
    return session.getIdToken().getJwtToken();
  }

  /**
   * Get user info from token
   */
  async getUserInfo(): Promise<CognitoUserInfo | null> {
    const session = await this.getSession();
    if (!session) return null;

    const idToken = session.getIdToken();
    const payload = idToken.payload;

    return {
      email: payload.email || localStorage.getItem('userEmail') || '',
      userId: payload.sub || '',
      givenName: payload.given_name || '',
      familyName: payload.family_name || '',
      tenantId: payload['custom:tenant_id'] || '',
      role: payload['custom:role'] || 'user',
    };
  }

  /**
   * Forgot password flow
   */
  async forgotPassword(email: string): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve({ success: true });
        },
        onFailure: (err: Error) => {
          resolve({ success: false, error: err.message });
        },
        inputVerificationCode: () => {
          resolve({ success: true });
        },
      });
    });
  }

  /**
   * Confirm forgot password
   */
  async confirmForgotPassword(
    email: string,
    verificationCode: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve({ success: true });
        },
        onFailure: (err: Error) => {
          resolve({ success: false, error: err.message });
        },
      });
    });
  }

  /**
   * Refresh session
   */
  async refreshSession(): Promise<boolean> {
    const user = userPool.getCurrentUser();
    if (!user) return false;

    return new Promise((resolve) => {
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(false);
          return;
        }

        const refreshToken = session.getRefreshToken();
        user.refreshSession(refreshToken, (refreshErr) => {
          resolve(!refreshErr);
        });
      });
    });
  }
}

export const cognitoAuth = new CognitoAuthService();
